import React, { Suspense, lazy } from 'react';
import { Calendar, MessageSquare, Share2, Globe, Users, Star, Headphones, Music2, Sparkles, Layers, Zap, Radio, Disc3, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { MagicCard } from '../UI/MagicCard';
import { motion } from 'framer-motion';

// Lazy load FloatingLines for performance (Three.js component)
const FloatingLines = lazy(() => import('../backgrounds/FloatingLines'));

export const FeaturesNightclub: React.FC = () => {
  const { t } = useTranslation(['home']);
  const { isBlack, colorScheme } = useTheme();

  const features = [
    {
      icon: Calendar,
      title: t('home:features.reservations.title'),
      description: t('home:features.reservations.description.nightclub'),
    },
    {
      icon: MessageSquare,
      title: t('home:features.crm.title'),
      description: t('home:features.crm.description.nightclub'),
    },
    {
      icon: Share2,
      title: t('home:features.social.title'),
      description: t('home:features.social.description'),
    },
    {
      icon: Globe,
      title: t('home:features.website.title'),
      description: t('home:features.website.description'),
    },
    {
      icon: Users,
      title: t('home:features.recruitment.title'),
      description: t('home:features.recruitment.description'),
    },
    {
      icon: Star,
      title: t('home:features.reviews.title'),
      description: t('home:features.reviews.description.nightclub'),
      multiStar: true,
    },
  ];

  // Styles adaptés aux thèmes
  const getHighlightCardStyle = () => {
    if (isBlack) {
      // Thème étoile (noir spatial)
      return 'bg-black/40 border-purple-500/30 shadow-[0_0_40px_rgba(168,85,247,0.15)]';
    }
    if (colorScheme === 'dark') {
      // Thème lune (dark)
      return 'bg-gray-800/90 border-purple-500/40 shadow-[0_0_30px_rgba(168,85,247,0.2)]';
    }
    // Thème soleil (light)
    return 'bg-white border-purple-200 shadow-xl';
  };

  const getIconBgStyle = () => {
    if (isBlack) return 'bg-purple-500/20';
    if (colorScheme === 'dark') return 'bg-purple-900/50';
    return 'bg-purple-100';
  };

  const getBadgeStyle = () => {
    return 'bg-gradient-to-r from-yellow-400 to-amber-500 text-black';
  };

  return (
    <section className={`relative py-20 overflow-hidden ${isBlack ? 'bg-transparent' : 'bg-white dark:bg-gray-900'}`}>
      {/* 🌌 FloatingLines Background - Moon theme only */}
      {colorScheme === 'dark' && (
        <Suspense fallback={null}>
          <FloatingLines
            linesGradient={["#7c3aed", "#c026d3", "#ec4899"]}
            enabledWaves={['middle', 'bottom']}
            lineCount={12}
            lineDistance={80}
            bendRadius={4.0}
            bendStrength={-1.0}
            interactive={false}
            parallax={false}
            animationSpeed={0.5}
            mixBlendMode="screen"
          />
        </Suspense>
      )}
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t('home:features.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            {t('home:features.subtitle')}
          </p>
        </div>

        {/* 🎧 KOMBY DJ - Highlight Card TOP */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className={`relative rounded-2xl border backdrop-blur-sm p-8 text-center ${getHighlightCardStyle()}`}>
            <div className="relative">
              {/* Badge EXCLUSIVE */}
              <div className="absolute -top-4 right-0">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold rounded-full ${getBadgeStyle()}`}>
                  <Sparkles className="w-3 h-3" />
                  {t('home:features.dj.badge')}
                </span>
              </div>

              {/* Icône */}
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getIconBgStyle()} mb-5`}>
                <Headphones className="w-8 h-8 text-purple-500 dark:text-purple-400" />
              </div>

              {/* Titre */}
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                {t('home:features.dj.title')}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-300 mb-6 max-w-2xl mx-auto">
                {t('home:features.dj.description')}
              </p>

              {/* Features */}
              <div className="flex flex-wrap justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${getIconBgStyle()}`}>
                    <Music2 className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('home:features.dj.aiMusic.title')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('home:features.dj.aiMusic.description')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${getIconBgStyle()}`}>
                    <Layers className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('home:features.dj.stems.title')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('home:features.dj.stems.description')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${getIconBgStyle()}`}>
                    <Disc3 className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{t('home:features.dj.autoScratch.title')}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('home:features.dj.autoScratch.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* 6 Feature Cards with Magic Bento effect */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {features.map((feature, index) => (
            <MagicCard 
              key={index} 
              className={`p-6 ${
                isBlack 
                  ? 'bg-black/40 border border-purple-500/20' 
                  : colorScheme === 'dark'
                    ? 'bg-gray-800/80 border border-purple-500/30'
                    : 'bg-white border border-gray-200 shadow-lg'
              }`}
              gradientColor={isBlack ? '#a855f7' : colorScheme === 'dark' ? '#7c3aed' : '#c084fc'}
              gradientOpacity={isBlack ? 0.25 : colorScheme === 'dark' ? 0.3 : 0.15}
              gradientSize={400}
              magnetism={false}
            >
              <div className="flex flex-col items-center text-center">
                <div className={`mb-4 p-4 rounded-full ${
                  isBlack 
                    ? 'bg-purple-500/20' 
                    : colorScheme === 'dark'
                      ? 'bg-purple-900/50'
                      : 'bg-purple-100'
                }`}>
                  <feature.icon className="h-8 w-8 text-purple-500 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {feature.description}
                </p>
              </div>
            </MagicCard>
          ))}
        </div>

        {/* 💊 KOMBY CAPSULE - Highlight Card BOTTOM */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className={`relative rounded-2xl border backdrop-blur-sm p-8 text-center ${getHighlightCardStyle()}`}>
            {/* Icône */}
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getIconBgStyle()} mb-5`}>
              <Package className="w-8 h-8 text-purple-500 dark:text-purple-400" />
            </div>

            {/* Titre */}
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('home:features.capsule.title')}
            </h3>

            {/* Slogan */}
            <p className="text-purple-500 dark:text-purple-400 font-medium italic mb-4">
              "{t('home:features.capsule.slogan')}"
            </p>

            {/* Description */}
            <p className="text-gray-600 dark:text-gray-300 mb-2 max-w-2xl mx-auto">
              {t('home:features.capsule.description')}
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 max-w-2xl mx-auto">
              {t('home:features.capsule.subdescription')}
            </p>

            {/* Features ASIO / MIDI / DVS */}
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-400/10 border border-yellow-400/30">
                <Zap className="w-4 h-4 text-yellow-500" />
                <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">ASIO</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-400/10 border border-cyan-400/30">
                <Radio className="w-4 h-4 text-cyan-500" />
                <span className="text-sm font-semibold text-cyan-600 dark:text-cyan-400">MIDI</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-pink-400/10 border border-pink-400/30">
                <Disc3 className="w-4 h-4 text-pink-500" />
                <span className="text-sm font-semibold text-pink-600 dark:text-pink-400">DVS</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

